/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { NgZone } from '@angular/core';
import { fromEvent, interval, merge, NEVER, Observable, of, combineLatest, defer } from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    map,
    share,
    shareReplay,
    startWith,
    switchMap,
    tap,
    mapTo,
    takeWhile
} from 'rxjs/operators';
import { BrowserSupportService } from 'browser-support/lib/browser-support.service';
import { BrowserLeanModeService } from 'crimson/lib/browser-lean-mode-service/browser-lean-mode.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { repeatedPress } from './gamepad-common-utils';
import { NvGamepadConfig } from './gamepad.config';
import { GamepadService } from './gamepad.service';
import { GamepadButtonName, MonitorOptions } from './gamepad.types';

export class WebApiGamepadService extends GamepadService {
    private readonly pollForGamepadConnection: boolean;
    private logger: Logger;

    /**
     * Emits an array of connected {@link Gamepad} at polling frequency
     */
    private gamepadsTick$: Observable<Gamepad[]>;

    private _isGamepadConnected$: Observable<boolean>;

    get isGamepadConnected$(): Observable<boolean> {
        return this._isGamepadConnected$;
    }

    get isAnyGamepadInUse$(): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.gamepadsTick$.pipe(
                map(gamepads => gamepads.some(gamepad => this.isAnyAxisTriggeredOrButtonPressed(gamepad))),
                distinctUntilChanged()
            )
        );
    }

    get leftPressed$(): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.gamepadsTick$.pipe(
                map(gamepads =>
                    gamepads.some(gamepad =>
                        this.isAxisTriggeredOrButtonPressed(
                            gamepad,
                            axes => axes[0] < -this.gamepadConfig.joystickMovementThreshold,
                            GamepadButtonName.DpadLeft
                        )
                    )
                ),
                distinctUntilChanged(),
                repeatedPress(this.gamepadConfig.continuousNavigationInterval)
            )
        );
    }

    get rightPressed$(): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.gamepadsTick$.pipe(
                map(gamepads =>
                    gamepads.some(gamepad =>
                        this.isAxisTriggeredOrButtonPressed(
                            gamepad,
                            axes => axes[0] > this.gamepadConfig.joystickMovementThreshold,
                            GamepadButtonName.DpadRight
                        )
                    )
                ),
                distinctUntilChanged(),
                repeatedPress(this.gamepadConfig.continuousNavigationInterval)
            )
        );
    }

    get upPressed$(): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.gamepadsTick$.pipe(
                map(gamepads =>
                    gamepads.some(gamepad =>
                        this.isAxisTriggeredOrButtonPressed(
                            gamepad,
                            axes => axes[1] < -this.gamepadConfig.joystickMovementThreshold,
                            GamepadButtonName.DpadUp
                        )
                    )
                ),
                distinctUntilChanged(),
                repeatedPress(this.gamepadConfig.continuousNavigationInterval)
            )
        );
    }

    get downPressed$(): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.gamepadsTick$.pipe(
                map(gamepads =>
                    gamepads.some(gamepad =>
                        this.isAxisTriggeredOrButtonPressed(
                            gamepad,
                            axes => axes[1] > this.gamepadConfig.joystickMovementThreshold,
                            GamepadButtonName.DpadDown
                        )
                    )
                ),
                distinctUntilChanged(),
                repeatedPress(this.gamepadConfig.continuousNavigationInterval)
            )
        );
    }

    get rightStickMoved$(): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.gamepadsTick$.pipe(
                map(gamepads =>
                    gamepads.some(gamepad =>
                        this.isAxisTriggeredOrButtonPressed(
                            gamepad,
                            axes =>
                                axes[2] > this.gamepadConfig.joystickTriggeredTolerance ||
                                axes[3] < -this.gamepadConfig.joystickTriggeredTolerance,
                            GamepadButtonName.RightStick
                        )
                    )
                ),
                distinctUntilChanged()
            )
        );
    }

    onButtonPressed$(button: GamepadButtonName, options?: MonitorOptions): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() => this.getButtonPressed$(this.gamepadsTick$, button, options));
    }

    onButtonLongPressed$(button: GamepadButtonName, longPressStartDuration?: number): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() =>
            this.getButtonPressed$(this.gamepadsTick$, button).pipe(
                switchMap(pressed => {
                    if (pressed) {
                        return of(true).pipe(delay(longPressStartDuration ?? this.gamepadConfig.longPressDuration));
                    } else {
                        return NEVER;
                    }
                })
            )
        );
    }

    onButtonComboPressed$(buttons: GamepadButtonName[]): Observable<boolean> {
        return this.ngZone.runOutsideAngular(() => this.getButtonComboPressed$(this.gamepadsTick$, buttons));
    }

    constructor(
        loggingService: LoggingService,
        leanModeService: BrowserLeanModeService,
        private ngZone: NgZone,
        private browserSupportService: BrowserSupportService,
        private gamepadConfig: NvGamepadConfig
    ) {
        super();
        this.pollForGamepadConnection = !!this.gamepadConfig?.pollForGamepadConnection;
        this.logger = loggingService.getLogger('WebApiGamepadService');

        const shouldIgnoreMapping = () =>
            /* Platform-check-disable-next-line */
            !!PlatformDetectorService.isAndroidPlatform(); // Ignored on Android Angular platform due to inconsistencies in the mapping value on various devices.

        const isStandardMapping = (gamepad: Gamepad) => gamepad?.mapping === 'standard';
        const isConnectedAndSupported = (gamepad: Gamepad) =>
            gamepad?.connected && (shouldIgnoreMapping() || isStandardMapping(gamepad));

        // Creates gamepad polling observables outside NgZone to prevent triggering change detection
        ngZone.runOutsideAngular(() => {
            let shouldPoll = this.pollForGamepadConnection;
            // in case 'gamepadconnected' doesn't fire after first gamepad input on AVP, we poll for connection until it is connected
            const pollForConnection$ = shouldPoll ? interval(1000) : NEVER;
            this._isGamepadConnected$ = merge(
                fromEvent(window, 'gamepadconnected'),
                fromEvent(window, 'gamepaddisconnected'),
                pollForConnection$.pipe(takeWhile(() => shouldPoll))
            ).pipe(
                startWith(0),
                map(_ => {
                    // Initial .getGamepads() call may return empty list on AVP, triggering a permission request banner.
                    // Non-empty results only appear after permission is granted and 'gamepadconnected' event fires.
                    return this.browserSupportService.getGamepads().some(isConnectedAndSupported);
                }),
                tap(hasAvailableGamepad => {
                    if (hasAvailableGamepad && this.pollForGamepadConnection) {
                        // Stop polling when a gamepad is first available
                        // subsequent gamepadconnected and gamepaddisconnected events will emit normally
                        shouldPoll = false;
                    }
                }),
                distinctUntilChanged(),
                shareReplay(1)
            );

            const tickPeriod$ = leanModeService.leanModeTransitionState.pipe(
                startWith(this.gamepadConfig.gamepadTickPeriodDefault),
                map(leanMode =>
                    leanMode ? this.gamepadConfig.gamepadTickPeriodLean : this.gamepadConfig.gamepadTickPeriodLean
                ),
                distinctUntilChanged()
            );

            const isInForeground$ = browserSupportService.applicationWindowState$.pipe(
                map(state => state === 'foreground')
            );
            const isWindowFocused$ = merge(
                defer(() => of(document.hasFocus())),
                fromEvent(window, 'focus').pipe(mapTo(true)),
                fromEvent(window, 'blur').pipe(mapTo(false))
            );

            /* Platform-check-disable-next-line */
            // NVBug 5578883: On some LG TV models, window focus/blur events may not fire correctly when
            // returning from LG homescreen, causing gamepads to stop working. In this case, skip the check
            // for window focused state and rely only on applicationWindowState$
            const isLGTV = PlatformDetectorService.isPWAModeOnLGTV();

            // Gamepad polling only starts when
            //   1. There is a connected gamepad
            //   2. `gamepadsTick$` is subscribed on
            this.gamepadsTick$ = combineLatest([this._isGamepadConnected$, isInForeground$, isWindowFocused$]).pipe(
                map(values => {
                    const connected = values[0];
                    const isInForeground = values[1];
                    const isWindowFocused = isLGTV ? true : values[2];
                    this.logger.info(
                        'connected=',
                        connected,
                        ', foreground=',
                        isInForeground,
                        ', windowFocusedWithLgtvWAR=',
                        isWindowFocused,
                        ', isWindowFocusedRealValue=',
                        values[2]
                    );
                    return connected && isInForeground && isWindowFocused;
                }),
                distinctUntilChanged(),
                switchMap(canPoll => {
                    if (canPoll) {
                        this.logger.info('Start polling gamepad state');
                        return tickPeriod$.pipe(
                            tap(period => {
                                this.logger.info(`Polling gamepad state with period: ${period} ms`);
                            }),
                            switchMap(period => interval(period)),
                            map(_ => browserSupportService.getGamepads().filter(isConnectedAndSupported))
                        );
                    } else {
                        this.logger.info('Stop polling gamepad state');
                        // Emits once to allow downstream observerables to reset states
                        return of([]);
                    }
                }),
                share()
            );
        });
    }

    /**
     * Returns an observable reflecting the pressed state of any desired combination of gamepad buttons
     *
     * @param gamepads$ gamepad polling observable
     * @param buttons an array that includes target buttons in the combination
     * @returns an observable that emits `true` when the button combination is pressed on any gamepad, otherwise emits `false`.
     */
    private getButtonComboPressed$(
        gamepads$: Observable<Gamepad[]>,
        buttons: GamepadButtonName[]
    ): Observable<boolean> {
        return gamepads$.pipe(
            map(gamepads => gamepads.some(gamepad => buttons.every(button => this.isButtonPressed(gamepad, button)))),
            distinctUntilChanged()
        );
    }

    // For non-standard mapping, use `options` to specify the button index and repeat flag
    private getButtonPressed$(
        gamepads$: Observable<Gamepad[]>,
        button: GamepadButtonName,
        options?: MonitorOptions
    ): Observable<boolean> {
        const repeat = !!options?.repeat;
        const buttonPressed$ = gamepads$.pipe(
            map(gamepads =>
                gamepads.some(gamepad => this.isButtonPressed(gamepad, button, options?.customButtonIndex))
            ),
            distinctUntilChanged()
        );
        return repeat
            ? buttonPressed$.pipe(repeatedPress(this.gamepadConfig.continuousNavigationInterval))
            : buttonPressed$;
    }

    // For non-standard mapping, use `customButtonIndex` to specify the button index
    private isButtonPressed(gamepad: Gamepad, button: GamepadButtonName, customButtonIndex?: number): boolean {
        const index = customButtonIndex ?? button;
        return gamepad.buttons[index].pressed;
    }

    private isAxisTriggeredOrButtonPressed(
        gamepad: Gamepad,
        isTriggered: (axes: readonly number[]) => boolean,
        button: GamepadButtonName
    ): boolean {
        return isTriggered(gamepad.axes) || this.isButtonPressed(gamepad, button);
    }

    private isAnyAxisTriggeredOrButtonPressed(gamepad: Gamepad): boolean {
        const buttonPressed = () => gamepad.buttons.some(button => button.pressed);
        const axisTriggered = () =>
            gamepad.axes.some(
                number =>
                    number > this.gamepadConfig.joystickTriggeredTolerance ||
                    number < -this.gamepadConfig.joystickTriggeredTolerance
            );
        return buttonPressed() || axisTriggered();
    }
}
